import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import api from '@utils/axios';
import I18n from '@utils/i18n';
import { Table, TableSize } from '@ui/molecules/Table';
import getColumns from './columns';
import { INITIAL_SORT, SORTABLE_COLUMNS } from './constants';

// IP-15563. The Summaries and Scopes of Work settings listings were plain ERB tables fetched with a
// bare `.all`, so Postgres returned them in arbitrary order. This is the Quimby Table used by the
// Tags settings grid, defaulting to name ascending -- alphabetised on load, re-sortable by column.
//
// Sorting is client-side on purpose. Tags pages and sorts through a Kendo datatable because tags are
// numerous; these lists are a dozen rows per tenant, so one fetch and a local sort is the whole job.
function TextLibraryIndex({ fetchPath, namespace, emptyKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [sort, setSort] = useState(INITIAL_SORT);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await api.get(fetchPath);
        if (!cancelled) {
          setRows(response.data.rows || []);
          setFailed(false);
        }
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [fetchPath]);

  const sortedRows = useMemo(() => {
    if (!sort?.field) return rows;

    const compare = (a, b) => {
      const left = a[sort.field];
      const right = b[sort.field];

      if (typeof left === 'boolean' || typeof right === 'boolean') {
        return Number(left) - Number(right);
      }

      // Case-insensitive so "admin test" and "ADMIN TEST" sit together, matching the
      // LOWER(name) ordering the server sends on first paint.
      return String(left ?? '').localeCompare(String(right ?? ''), undefined, { sensitivity: 'base' });
    };

    const ordered = [...rows].sort(compare);
    return sort.dir === 'desc' ? ordered.reverse() : ordered;
  }, [rows, sort]);

  const onSort = useCallback((field, direction) => {
    setSort(direction ? { field, dir: direction } : null);
  }, []);

  const columns = useMemo(() => getColumns(namespace), [namespace]);

  return (
    <Table
      data={sortedRows}
      columns={columns}
      variant="detail"
      size={TableSize.Full}
      getItemId={(row) => row.id}
      sortable
      sortableColumns={SORTABLE_COLUMNS}
      onSort={onSort}
      sortBy={sort?.field ?? null}
      sortDirection={sort?.dir ?? null}
      isLoading={loading}
      loadingLabel={I18n.t('generic.loading')}
      isError={failed}
      emptyContent={I18n.t(emptyKey)}
    />
  );
}

TextLibraryIndex.propTypes = {
  fetchPath: PropTypes.string.isRequired,
  namespace: PropTypes.string.isRequired,
  emptyKey: PropTypes.string.isRequired
};

export default TextLibraryIndex;
